import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, toUploadUrl } from '../../config/api';

const SiteForm = ({ site, locations, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        location_id: site?.location_id || '',
        name: site?.name || '',
        slug: site?.slug || '',
        site_type: site?.site_type || 'tent',
        capacity: site?.capacity || 2,
        price_per_night: site?.price_per_night || '',
        discount_price: site?.discount_price || '',
        description: site?.description || '',
        bed_type: site?.bed_type || 'Sleeping Bag',
        size_sqm: site?.size_sqm || '',
        image: site?.image || '',
        gallery: site?.gallery || '',
        amenities: site?.amenities || '',
        
        // Facilities
        has_electricity: site?.has_electricity || 0,
        has_water: site?.has_water || 0,
        has_wifi: site?.has_wifi || 0,
        has_parking: site?.has_parking || 0,
        has_fire_pit: site?.has_fire_pit || 1,
        pet_friendly: site?.pet_friendly || 0,
        
        status: site?.status || 'available'
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    // ✅ Image upload states
    const [mainImageFile, setMainImageFile] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState('');
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);
    const [existingGalleryImages, setExistingGalleryImages] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [imagesToDelete, setImagesToDelete] = useState([]);

    // ✅ Load existing images when editing
    useEffect(() => {
        if (site) {
            // Load main image
            if (site.image) {
                setMainImagePreview(toUploadUrl(site.image, { camping: true }));
            }
            
            // Load gallery images
            if (site.gallery) {
                const galleryArray = site.gallery.split(',').filter(img => img.trim());
                const galleryUrls = galleryArray.map((img) => toUploadUrl(img.trim(), { camping: true }));
                setExistingGalleryImages(galleryUrls);
                setGalleryPreviews(galleryUrls);
            }
        }
    }, [site]);

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please login to perform this action');
            window.location.href = '/admin/login';
            return null;
        }
        return token;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value 
        }));

        if (name === 'name' && !site) {
            const slug = value.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    };

    // ✅ Handle main image selection
    const handleMainImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should be less than 5MB');
                return;
            }
            
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }
            
            setMainImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setMainImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // ✅ Remove existing main image
    const removeExistingMainImage = () => {
        if (site?.image) {
            setImagesToDelete(prev => [...prev, site.image]);
        }
        setMainImagePreview('');
        setMainImageFile(null);
        setFormData(prev => ({ ...prev, image: '' }));
    };

    // ✅ Handle gallery images selection
    const handleGalleryChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = [];
        const previews = [];

        for (const file of files) {
            if (file.size > 5 * 1024 * 1024) {
                alert(`File ${file.name} is too large. Max 5MB`);
                continue;
            }
            
            if (!file.type.startsWith('image/')) {
                alert(`File ${file.name} is not an image`);
                continue;
            }
            
            validFiles.push(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                previews.push(reader.result);
                if (previews.length === validFiles.length) {
                    setGalleryPreviews([...galleryPreviews, ...previews]);
                }
            };
            reader.readAsDataURL(file);
        }
        
        setGalleryFiles([...galleryFiles, ...validFiles]);
    };

    // ✅ Remove existing gallery image
    const removeExistingGalleryImage = (index, imagePath) => {
        // Extract filename from URL
        const filename = imagePath.split('/').pop();
        setImagesToDelete(prev => [...prev, filename]);
        
        const newPreviews = galleryPreviews.filter((_, i) => i !== index);
        setGalleryPreviews(newPreviews);
        
        // Also remove from existingGalleryImages if it was existing
        if (existingGalleryImages.includes(imagePath)) {
            const newExisting = existingGalleryImages.filter((_, i) => i !== index);
            setExistingGalleryImages(newExisting);
        }
    };

    // ✅ Remove new gallery image (not yet uploaded)
    const removeNewGalleryImage = (index) => {
        const newFiles = galleryFiles.filter((_, i) => i !== index);
        const newPreviews = galleryPreviews.filter((_, i) => i !== index);
        setGalleryFiles(newFiles);
        setGalleryPreviews(newPreviews);
    };

    // ✅ Upload images to server
    const uploadImages = async () => {
        const uploadedImages = {
            mainImage: null,
            galleryImages: []
        };
        
        // Upload main image if new file selected
        if (mainImageFile) {
            const formData = new FormData();
            formData.append('image', mainImageFile);
            formData.append('folder', 'camping');
            
            try {
                const token = getToken();
                const response = await axios.post(buildApiUrl('/upload'), formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    }
                });
                
                if (response.data.success) {
                    uploadedImages.mainImage = response.data.filename;
                }
            } catch (error) {
                console.error('Error uploading main image:', error);
                alert('Failed to upload main image');
                return null;
            }
        }
        
        // Upload new gallery images
        for (const file of galleryFiles) {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('folder', 'camping/gallery');
            
            try {
                const token = getToken();
                const response = await axios.post(buildApiUrl('/upload'), formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                if (response.data.success) {
                    uploadedImages.galleryImages.push(response.data.filename);
                }
            } catch (error) {
                console.error('Error uploading gallery image:', error);
                alert('Failed to upload gallery image');
                return null;
            }
        }
        
        // Keep existing gallery images that were not deleted
        const keepExistingImages = existingGalleryImages.map(img => img.split('/').pop());
        
        return {
            mainImage: uploadedImages.mainImage || (imagesToDelete.includes(formData.image) ? null : formData.image),
            galleryImages: [...keepExistingImages, ...uploadedImages.galleryImages].join(',')
        };
    };

    // ✅ Delete images from server
    const deleteImages = async () => {
        if (imagesToDelete.length === 0) return;
        
        const token = getToken();
        for (const filename of imagesToDelete) {
            try {
                await axios.delete(buildApiUrl(`/upload/${filename}`), {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params: { folder: 'camping' }
                });
            } catch (error) {
                console.error('Error deleting image:', filename, error);
            }
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.slug) newErrors.slug = 'Slug is required';
        if (!formData.location_id) newErrors.location_id = 'Location is required';
        if (!formData.price_per_night) newErrors.price_per_night = 'Price is required';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setUploadProgress(0);
        
        try {
            const token = getToken();
            if (!token) return;

            // First, delete images that were removed
            await deleteImages();

            // Upload new images
            let images = {};
            if (mainImageFile || galleryFiles.length > 0 || imagesToDelete.length > 0) {
                images = await uploadImages();
                if (!images) {
                    setLoading(false);
                    return;
                }
            }
            
            // Prepare data for submission
            const submitData = {
                ...formData,
                image: images.mainImage || formData.image,
                gallery: images.galleryImages || formData.gallery
            };
            
            // Remove empty values
            Object.keys(submitData).forEach(key => {
                if (submitData[key] === '' || submitData[key] === null) {
                    delete submitData[key];
                }
            });
            
            const url = site 
                ? buildApiUrl(`/camping/admin/sites/${site.id}/update`)
                : buildApiUrl('/camping/admin/sites');

            const headers = { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            if (site) {
                await axios.post(url, submitData, { headers });
                alert('✅ Site updated successfully');
            } else {
                await axios.post(url, submitData, { headers });
                alert('✅ Site created successfully');
            }
            
            onSuccess();
        } catch (error) {
            console.error('❌ Error:', error);
            
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert(error.response?.data?.message || 'Failed to save site');
            }
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const siteTypes = ['Agro Farms','Villa','Waterpark','tent','cabin','glamping','camping','Tree House','House Boat','Domes'];
    const bedTypes = ['Sleeping Bag', 'Mattress', 'Cot', 'Single Bed', 'Double Bed', 'Queen Bed', 'King Bed'];

    return (
        <div className="modal-overlay">
            <div className="modal modal-lg">
                <div className="modal-header">
                    <h3>{site ? 'Edit Camping Site' : 'Add New Camping Site'}</h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-tabs">
                        <div className="tab-content">
                            {/* Basic Information - Same as before */}
                            <div className="form-section">
                                <h4>Basic Information</h4>
                                <div className="form-grid">
                                    <div className="form-group full-width">
                                        <label>Location *</label>
                                        <select 
                                            name="location_id" 
                                            value={formData.location_id} 
                                            onChange={handleChange}
                                            className={errors.location_id ? 'error' : ''}
                                        >
                                            <option value="">Select Location</option>
                                            {locations.map(loc => (
                                                <option key={loc.id} value={loc.id}>
                                                    {loc.name} - {loc.city || loc.location_address || 'Kashmir'}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.location_id && <span className="error-text">{errors.location_id}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Site Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={errors.name ? 'error' : ''}
                                            placeholder="e.g., Meadow View Tent"
                                        />
                                        {errors.name && <span className="error-text">{errors.name}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Slug *</label>
                                        <input
                                            type="text"
                                            name="slug"
                                            value={formData.slug}
                                            onChange={handleChange}
                                            className={errors.slug ? 'error' : ''}
                                            placeholder="meadow-view-tent"
                                        />
                                        {errors.slug && <span className="error-text">{errors.slug}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Site Type</label>
                                        <select name="site_type" value={formData.site_type} onChange={handleChange}>
                                            {siteTypes.map(type => (
                                                <option key={type} value={type}>
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Capacity (persons)</label>
                                        <input
                                            type="number"
                                            name="capacity"
                                            value={formData.capacity}
                                            onChange={handleChange}
                                            min="1"
                                            max="20"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Bed Type</label>
                                        <select name="bed_type" value={formData.bed_type} onChange={handleChange}>
                                            {bedTypes.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Size (sq.m)</label>
                                        <input
                                            type="number"
                                            name="size_sqm"
                                            value={formData.size_sqm}
                                            onChange={handleChange}
                                            placeholder="e.g., 20"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Pricing - Same as before */}
                            <div className="form-section">
                                <h4>Pricing</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Price per Night (₹) *</label>
                                        <input
                                            type="number"
                                            name="price_per_night"
                                            value={formData.price_per_night}
                                            onChange={handleChange}
                                            className={errors.price_per_night ? 'error' : ''}
                                            placeholder="e.g., 2000"
                                            step="0.01"
                                        />
                                        {errors.price_per_night && <span className="error-text">{errors.price_per_night}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Discount Price (₹)</label>
                                        <input
                                            type="number"
                                            name="discount_price"
                                            value={formData.discount_price}
                                            onChange={handleChange}
                                            placeholder="e.g., 1800"
                                            step="0.01"
                                        />
                                        <small>Leave empty if no discount</small>
                                    </div>
                                </div>
                            </div>

                            {/* Description - Same as before */}
                            <div className="form-section">
                                <h4>Description</h4>
                                <div className="form-group">
                                    <textarea
                                        name="description"
                                        rows="4"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Describe the camping site, views, experience..."
                                    ></textarea>
                                </div>
                            </div>

                            {/* ✅ IMAGE UPLOAD SECTION WITH EXISTING IMAGES */}
                            <div className="form-section">
                                <h4>Images</h4>
                                
                                {/* Main Image Upload */}
                                <div className="form-group">
                                    <label>Main Image</label>
                                    <div className="image-upload-area">
                                        <div className="image-preview">
                                            {mainImagePreview ? (
                                                <div className="preview-container">
                                                    <img src={mainImagePreview} alt="Main preview" />
                                                    <button 
                                                        type="button"
                                                        className="remove-image"
                                                        onClick={removeExistingMainImage}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <i className="fas fa-cloud-upload-alt"></i>
                                                    <p>Click or drag to upload main image</p>
                                                    <small>Max size: 5MB. Supported: JPG, PNG, GIF</small>
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleMainImageChange}
                                            className="image-input"
                                            id="main-image"
                                        />
                                        <label htmlFor="main-image" className="upload-btn">
                                            <i className="fas fa-upload"></i> {mainImagePreview ? 'Change Image' : 'Choose Image'}
                                        </label>
                                    </div>
                                    {site?.image && mainImagePreview && (
                                        <small className="existing-image-note">Current image: {site.image}</small>
                                    )}
                                </div>

                                {/* Gallery Images Upload */}
                                <div className="form-group">
                                    <label>Gallery Images</label>
                                    <div className="gallery-upload-area">
                                        <div className="gallery-preview-grid">
                                            {galleryPreviews.map((preview, index) => {
                                                const isExisting = existingGalleryImages.includes(preview);
                                                return (
                                                    <div key={index} className="gallery-preview-item">
                                                        <img src={preview} alt={`Gallery ${index + 1}`} />
                                                        <button 
                                                            type="button"
                                                            className="remove-gallery-image"
                                                            onClick={() => isExisting 
                                                                ? removeExistingGalleryImage(index, preview)
                                                                : removeNewGalleryImage(index)
                                                            }
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                        {isExisting && (
                                                            <span className="existing-badge">Existing</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <label htmlFor="gallery-images" className="add-more-btn">
                                                <i className="fas fa-plus"></i>
                                                <span>Add More</span>
                                            </label>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleGalleryChange}
                                            className="gallery-input"
                                            id="gallery-images"
                                        />
                                    </div>
                                    <small>You can select multiple images. Max 5MB each. Existing images will be kept unless removed.</small>
                                </div>

                                {/* Upload Progress */}
                                {loading && uploadProgress > 0 && (
                                    <div className="upload-progress">
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                        <span>{uploadProgress}% uploaded</span>
                                    </div>
                                )}
                            </div>

                            {/* Amenities - Same as before */}
                            <div className="form-section">
                                <h4>Amenities</h4>
                                <div className="amenities-checkbox-grid">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="has_electricity"
                                            checked={formData.has_electricity === 1}
                                            onChange={handleChange}
                                        />
                                        <span>Electricity</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="has_water"
                                            checked={formData.has_water === 1}
                                            onChange={handleChange}
                                        />
                                        <span>Water Supply</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="has_wifi"
                                            checked={formData.has_wifi === 1}
                                            onChange={handleChange}
                                        />
                                        <span>WiFi</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="has_parking"
                                            checked={formData.has_parking === 1}
                                            onChange={handleChange}
                                        />
                                        <span>Parking</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="has_fire_pit"
                                            checked={formData.has_fire_pit === 1}
                                            onChange={handleChange}
                                        />
                                        <span>Fire Pit</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="pet_friendly"
                                            checked={formData.pet_friendly === 1}
                                            onChange={handleChange}
                                        />
                                        <span>Pet Friendly</span>
                                    </label>
                                </div>
                            </div>

                            {/* Status - Same as before */}
                            <div className="form-section">
                                <h4>Status</h4>
                                <div className="form-group">
                                    <select name="status" value={formData.status} onChange={handleChange}>
                                        <option value="available">Available</option>
                                        <option value="booked">Booked</option>
                                        <option value="maintenance">Under Maintenance</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? (uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : 'Saving...') : (site ? 'Update Site' : 'Add Site')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SiteForm;
